/**
 * A product's picture, and what stands in for one when the catalog has no photo yet.
 *
 * "No image" in grey was the old answer, printed twelve times down a grid, and it reads as
 * a broken page rather than a shop. A catalog is loaded long before anybody photographs it
 * -- the ERP has always allowed `images` to be null -- so the empty case is the normal
 * case, not an error, and it should look deliberate.
 *
 * The stand-in is the product's initials on a tint picked from its own name, so two
 * products next to each other never come out the same colour and the same product is the
 * same colour on every screen it appears on. Six tints, all drawn from the palette the ERP
 * already uses, and all legible in either theme.
 */

import { useEffect, useState } from 'react'

import { apiUrl } from '@/api/client'

/** Tailwind pairs (background, text), light and dark, from the app's own palette. */
const TINTS = [
  'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
]

/**
 * Two letters that tell one product from its neighbours.
 *
 * Not the first two words: an FMCG catalog is brand-led, so "Annapurna Best Basmati Rice",
 * "Annapurna Best Iodised Salt" and "Annapurna Best Sunflower Oil" all reduce to "AB" and
 * the grid comes out reading the same word four times. The brand plus the noun -- the first
 * word and the last one that is not a pack size -- separates them: AR, AS, AO.
 */
function initials(name: string): string {
  const words = name
    .split(/\s+/)
    // "1kg", "6x500ml", "500g": a pack size names the tin, never the thing in it.
    .filter((word) => /^[a-z]/i.test(word) && !/\d/.test(word))
  if (words.length === 0) return name.trim().slice(0, 2).toUpperCase() || '·'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Same name, same tint, on every screen — a sum over the characters, not a random pick. */
function tintOf(name: string): string {
  let total = 0
  for (let i = 0; i < name.length; i += 1) total += name.charCodeAt(i)
  return TINTS[total % TINTS.length]
}

export function ProductImage({
  src,
  name,
  className = '',
  textClass = 'text-lg',
}: {
  src?: string | null
  name: string
  /** Sizing and rounding come from the caller — a grid tile and a cart line differ. */
  className?: string
  /** How big the initials are, matched to that size. */
  textClass?: string
}) {
  // A stored photo is reached through a signed URL that stops working after a while, and the
  // cart keeps its line images in localStorage -- so a shopper who leaves a full cart
  // overnight comes back to links that have expired. Falling back to the initials keeps that
  // looking like a shop rather than like a page of broken-image icons.
  const [broken, setBroken] = useState(false)
  useEffect(() => setBroken(false), [src])

  if (src && !broken) {
    return (
      <img
        src={apiUrl(src)}
        alt={name}
        onError={() => setBroken(true)}
        className={`h-full w-full object-cover ${className}`}
      />
    )
  }
  return (
    <div
      // `aria-hidden`: the product's name is always beside it, and "AB" read aloud tells a
      // screen-reader user nothing the next line does not.
      aria-hidden
      className={`flex h-full w-full items-center justify-center font-semibold tracking-wide ${textClass} ${tintOf(name)} ${className}`}
    >
      {initials(name)}
    </div>
  )
}
